import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface SemanticRow {
  readonly definitionId: string;
  readonly entityName: string;
  readonly businessName: string;
  readonly expression: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'PUBLISHED', 'DEPRECATED'];

/**
 * GR L26.06 — Semantic Layer (`/dwh/semantic-layer`, admin). The governed
 * business-metric dictionary: each `semantic_layer_definitions` row mapping a
 * business name to its underlying SQL/DSL expression and publish status. This
 * is the definition set the Mart Explorer (L26.02) enforces. Admin-only surface;
 * filterable by publish status.
 */
export function GrSemanticLayerPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<SemanticRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: SemanticRow[] }>(`/v1/dwh/governance/semantic-layer${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status]);

  return (
    <main aria-label="semantic-layer">
      <h1>{t('gr26.semantic.title')}</h1>
      <div className="filters">
        <label>
          {t('gr26.semantic.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr26.semantic.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr26.semantic.loading')}</p> : (
        <>
          <p aria-label="definition-count">{t('gr26.semantic.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr26.semantic.empty')}</p> : (
            <table aria-label="semantic-table">
              <thead><tr><th>{t('gr26.semantic.entity')}</th><th>{t('gr26.semantic.businessName')}</th><th>{t('gr26.semantic.expression')}</th><th>{t('gr26.semantic.status')}</th></tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.definitionId}>
                    <td>{d.entityName}</td><td>{d.businessName}</td>
                    <td><code>{d.expression}</code></td><td>{d.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
